/**
 * Which sign-in method this browser used last, for the "Last used" badge on
 * the login page.
 *
 * Per-BROWSER on purpose, not per-account. An account-level memory would follow
 * the user to any machine, which on a shared or public computer means the login
 * screen tells the NEXT person which method that user signs in with. A
 * browser-local memory only ever tells you about yourself. It also needs no API,
 * so nothing here waits on a backend change.
 *
 * The cost — a new machine shows no badge until the first sign-in there — is
 * the correct behaviour anyway: we genuinely do not know yet.
 *
 * Every storage access is guarded. Private windows and locked-down browsers
 * throw on ACCESS, not merely on write, and a login page must never fail to
 * render over a convenience feature.
 */

export type LoginMethod =
  | 'password'
  | 'otp'
  | 'passkey'
  | 'microsoft'
  | 'google'

const METHODS: readonly string[] = [
  'password',
  'otp',
  'passkey',
  'microsoft',
  'google',
]

/** Only these two go through the staged (leave-the-page) flow. */
const SSO_METHODS: readonly string[] = ['microsoft', 'google']

export const LAST_USED_STORAGE_KEY = 'hf-last-used-login-method'
export const SSO_PENDING_STORAGE_KEY = 'hf-pending-sso-method'

type StoreName = 'localStorage' | 'sessionStorage'

/**
 * Every access is guarded, including READS. Private windows and locked-down
 * browsers throw on the property access itself, not merely on write, and a
 * login page must never fail to render over a convenience feature.
 */
function read(store: StoreName, key: string): string | null {
  try {
    return window[store].getItem(key)
  } catch {
    return null
  }
}

function write(store: StoreName, key: string, value: string): void {
  try {
    window[store].setItem(key, value)
  } catch {
    /* blocked or full — the badge is optional, never block a sign-in */
  }
}

function remove(store: StoreName, key: string): void {
  try {
    window[store].removeItem(key)
  } catch {
    /* as above */
  }
}

/**
 * The method this browser last signed in with, or null when there is nothing
 * to show. A stored value that is not a method we recognise (stale key, hand
 * edit) reads as null rather than rendering a badge against nothing.
 */
export function getLastUsedMethod(): LoginMethod | null {
  if (typeof window === 'undefined') return null
  const raw = read('localStorage', LAST_USED_STORAGE_KEY)
  return raw && METHODS.includes(raw) ? (raw as LoginMethod) : null
}

/** Call on a CONFIRMED sign-in, never on an attempt. */
export function recordLastUsedMethod(method: LoginMethod): void {
  if (typeof window === 'undefined') return
  write('localStorage', LAST_USED_STORAGE_KEY, method)
}

/**
 * Called when the operator clicks Microsoft or Google. They are about to leave
 * the page entirely, so we cannot know yet whether they will authenticate or
 * abandon at the provider — this only STAGES the intent.
 *
 * sessionStorage rather than localStorage so an abandoned attempt dies with the
 * tab instead of lingering.
 */
export function stageSsoAttempt(method: 'microsoft' | 'google'): void {
  if (typeof window === 'undefined') return
  write('sessionStorage', SSO_PENDING_STORAGE_KEY, method)
}

/**
 * Called from the SSO callback once the provider has actually confirmed the
 * sign-in. Promotes a staged attempt to the real record and clears the marker,
 * so a reload of the callback cannot re-credit it.
 *
 * Returns the promoted method, or null when there was nothing genuine to
 * promote — in which case whatever worked last is left untouched.
 */
export function confirmStagedSsoAttempt(): LoginMethod | null {
  if (typeof window === 'undefined') return null
  const pending = read('sessionStorage', SSO_PENDING_STORAGE_KEY)
  remove('sessionStorage', SSO_PENDING_STORAGE_KEY)
  if (!pending || !SSO_METHODS.includes(pending)) return null
  const method = pending as LoginMethod
  recordLastUsedMethod(method)
  return method
}
